import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { initializeClarity } from "@/lib/clarity";
import { AuthProvider, useOptionalAuth, useAuth } from "@/components/AuthProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SuperAdminRoute } from "@/components/SuperAdminRoute";
import { AgencyRoute } from "@/components/AgencyRoute";
import { RoleRoute } from "@/components/RoleRoute";
import { IntegrationRoute } from "@/components/IntegrationRoute";
import { FeatureRoute } from "@/components/FeatureRoute";
import { BusinessAccountProvider } from "@/contexts/BusinessAccountContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { CallProvider } from "@/contexts/CallContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { UnreadMessagesProvider } from "@/contexts/UnreadMessagesContext";
import { MessageStatusProvider } from "@/contexts/MessageStatusContext";
import { WidgetConfigProvider } from "@/contexts/WidgetConfigContext";
import { NavigationProvider } from "@/contexts/NavigationContext";
import { AppShell } from "./components/layout/AppShell";
import { GlobalSyncAnimator } from "./components/GlobalSyncAnimator";
import { IncomingCallModal } from "./components/call/IncomingCallModal";
import { ActiveCallWindow } from "./components/call/ActiveCallWindow";
import { Dashboard } from "./pages/Dashboard";
import {
  cacheOnboardingCompleted,
  isFirstRunOnboardingAudience,
  isNewSignupPending,
  isOnboardingCompletedLocally,
  isOnboardingCompletePending,
  markOnboardingComplete,
  subscriptionProvesPriorSetup,
} from "@/lib/onboardingCompletion";
import { useBusinessAccountContext } from "@/hooks/useBusinessAccountContext";
import {
  ONBOARDING_V2_DASHBOARD_PATH,
  ONBOARDING_V2_LEGACY_DASHBOARD_PATH,
  ONBOARDING_V2_LEGACY_PREFLIGHT_PATH,
  ONBOARDING_V2_PREFLIGHT_PATH,
} from "@/components/onboarding-v2/onboardingV2Routes";
import { OnboardingFirstRunGuard } from "@/components/onboarding-v2/OnboardingFirstRunGuard";

// Lazy-loaded heavy pages (code splitting) — loaded only when route is visited
const AgentDashboard = lazy(() => import("./pages/AgentDashboard"));
const ChannelChat = lazy(() => import("./pages/channels/ChannelChat"));
// /home/inbox is retired in favour of Channels — this redirects old deep-links.
const InboxRedirect = lazy(() => import("./pages/channels/InboxRedirect"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Tickets = lazy(() => import("./pages/Tickets"));
const ManageTicketStatuses = lazy(() => import("./pages/tickets/ManageTicketStatuses"));
const Templates = lazy(() => import("./pages/Templates").then(m => ({ default: m.Templates })));
const Campaigns = lazy(() => import("./pages/Campaigns").then(m => ({ default: m.Campaigns })));
// TEMPORARY preview routes for the Campaigns/Templates redesign — see the
// 'new-campaigns' sidebar group in AppSidebar.tsx for context. Remove both
// once the redesign is cut over onto the real /home/campaigns and
// /home/templates routes.
const NewCampaigns = lazy(() => import("./pages/NewCampaigns"));
const NewTemplates = lazy(() => import("./pages/NewTemplates"));
const Analytics = lazy(() => import("./pages/Analytics").then(m => ({ default: m.Analytics })));
const Flows = lazy(() => import("./pages/automation/Flows").then(m => ({ default: m.Flows })));
const FlowBuilder = lazy(() => import("./pages/automation/FlowBuilder").then(m => ({ default: m.FlowBuilder })));
const InstagramFlowBuilder = lazy(() => import("./pages/automation/instagram/InstagramFlowBuilder"));
const InstagramFlowAnalytics = lazy(() => import("./pages/automation/instagram/InstagramFlowAnalytics"));

/** Old deep-link → Automations flows analytics path (keeps Automations sidebar active). */
function InstagramAnalyticsLegacyRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/home/automation/flows/${id}/analytics`} replace />;
}

/** Legacy IG flows list → unified Automations workspace with Instagram tab. */
function InstagramFlowsListRedirect() {
  const [params] = useSearchParams();
  const qs = new URLSearchParams({ platform: 'instagram' });
  const accountId = params.get('account_id') || params.get('account');
  if (accountId) qs.set('account_id', accountId);
  return <Navigate to={`/home/automation?${qs.toString()}`} replace />;
}
/** Legacy FB flows list → unified Automations workspace with Facebook tab. */
function FacebookFlowsListRedirect() {
  const [params] = useSearchParams();
  const qs = new URLSearchParams({ platform: 'facebook' });
  const accountId = params.get('account_id') || params.get('account');
  if (accountId) qs.set('account_id', accountId);
  return <Navigate to={`/home/automation?${qs.toString()}`} replace />;
}
/** Legacy standalone Instagram Posts page → the Instagram tab of the unified
    Social Media Posts workspace, which is the actively-maintained implementation
    (SocialMediaPostsManager no longer renders the old InstagramPosts component —
    see socialPostsCreateSignal.test.ts). Same pattern as the `linkedin/posts`
    redirect below. */
function InstagramPostsLegacyRedirect() {
  const [params] = useSearchParams();
  const qs = new URLSearchParams({ platform: 'instagram' });
  const accountId = params.get('account_id') || params.get('account');
  if (accountId) qs.set('account_id', accountId);
  return <Navigate to={`/home/social-media/posts?${qs.toString()}`} replace />;
}
/** Legacy standalone Facebook Posts page → the Facebook tab of the unified
    Social Media Posts workspace. Same pattern as InstagramPostsLegacyRedirect. */
function FacebookPostsLegacyRedirect() {
  const [params] = useSearchParams();
  const qs = new URLSearchParams({ platform: 'facebook' });
  const accountId = params.get('account_id') || params.get('account');
  if (accountId) qs.set('account_id', accountId);
  return <Navigate to={`/home/social-media/posts?${qs.toString()}`} replace />;
}
const FacebookFlowBuilder = lazy(() => import("./pages/automation/facebook/FacebookFlowBuilder"));
const FacebookFlowAnalytics = lazy(() => import("./pages/automation/facebook/FacebookFlowAnalytics"));
const FlowExecutions = lazy(() => import("./pages/automation/FlowExecutions"));
const Runs = lazy(() => import("./pages/automation/Runs").then(m => ({ default: m.Runs })));
const Commerce = lazy(() => import("./pages/Commerce").then(m => ({ default: m.Commerce })));
const CommerceDashboardPage = lazy(() => import("./pages/CommerceDashboardPage").then(m => ({ default: m.CommerceDashboardPage })));
const CommerceV2 = lazy(() => import("./pages/CommerceV2").then(m => ({ default: m.CommerceV2 })));
const GenUIWorkspace = lazy(() => import("./pages/GenUIWorkspace"));
const TeamChat = lazy(() => import("./pages/TeamChat"));
const Agents = lazy(() => import("./pages/Agents").then(m => ({ default: m.Agents })));
const VoiceAgents = lazy(() => import("./pages/VoiceAgents"));
const Settings = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const AccountSettings = lazy(() => import("./pages/AccountSettings").then(m => ({ default: m.AccountSettings })));
const Integrations = lazy(() => import("./pages/Integrations"));
const Billing = lazy(() => import("./pages/Billing").then(m => ({ default: m.Billing })));
const ShortLinks = lazy(() => import("./pages/ShortLinks").then(m => ({ default: m.ShortLinks })));
const ShortLinkDetail = lazy(() => import("./pages/ShortLinkDetail").then(m => ({ default: m.ShortLinkDetail })));
const CompetitorAds = lazy(() => import("./pages/CompetitorAds").then(m => ({ default: m.CompetitorAds })));
const AddCustomer = lazy(() => import("./pages/AddCustomer").then(m => ({ default: m.AddCustomer })));
const TeamManagement = lazy(() => import("./pages/TeamManagement"));
import TeamDemo from "./pages/TeamDemo";
const BusinessAccounts = lazy(() => import("./pages/BusinessAccounts"));
const Notifications = lazy(() => import("./pages/Notifications"));
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
import Auth from "./pages/Auth";
import GetApp from "./pages/GetApp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MetaCallback from "./pages/MetaCallback";
import { SubscriptionGuard } from "./components/SubscriptionGuard";
import ShopifyCallback from "./pages/ShopifyCallback";
import WaConnect from "./pages/WaConnect";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import { LandingPrivacyPolicy } from "./pages/LandingPrivacyPolicy";
import { LandingTermsOfService } from "./pages/LandingTermsOfService";
import Shopify from "./pages/Shopify";
import WooCommerce from "./pages/WooCommerce";
import Razorpay from "./pages/Razorpay";
import RazorpayCallback from "./pages/RazorpayCallback";
import Payu from "./pages/Payu";
import PaymentReturn from "./pages/PaymentReturn";
import WebsiteBuilderDashboard from "./pages/website-builder/WebsiteBuilderDashboard";
import WebsiteBuilderEditor from "./pages/website-builder/WebsiteBuilderEditor";
import Cashfree from "./pages/Cashfree";
import UberDirect from "./pages/UberDirect";
const WhatsAppFlows = lazy(() => import("./pages/WhatsAppFlows").then(m => ({ default: m.WhatsAppFlows })));
const WhatsAppFlowBuilder = lazy(() => import("./pages/WhatsAppFlowBuilder").then(m => ({ default: m.WhatsAppFlowBuilder })));
const GoogleSheets = lazy(() => import("./pages/GoogleSheets"));
// Auth pages — not lazy (needed immediately)
import { AdminLogin } from "./pages/admin/AdminLogin";
import { AgencyLogin } from "./pages/agency/AgencyLogin";
import { AccessDenied } from "./pages/admin/AccessDenied";
// Admin pages — lazy loaded
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard").then(m => ({ default: m.AdminDashboard })));
const AdminClients = lazy(() => import("./pages/admin/Clients").then(m => ({ default: m.AdminClients })));
const AdminInternalAccounts = lazy(() => import("./pages/admin/InternalAccounts"));
const ClientDetails = lazy(() => import("./pages/admin/ClientDetails").then(m => ({ default: m.ClientDetails })));
const OrganizationPlanManagement = lazy(() => import("./pages/admin/OrganizationPlanManagement").then(m => ({ default: m.OrganizationPlanManagement })));
const AdminSubscriptions = lazy(() => import("./pages/admin/Subscriptions").then(m => ({ default: m.AdminSubscriptions })));
const AdminIntegrations = lazy(() => import("./pages/admin/Integrations").then(m => ({ default: m.AdminIntegrations })));
const AdminSettings = lazy(() => import("./pages/admin/Settings").then(m => ({ default: m.AdminSettings })));
const AdminAgencies = lazy(() => import("./pages/admin/Agencies"));
const AdminLibrary = lazy(() => import("./pages/admin/AdminLibrary").then(m => ({ default: m.AdminLibrary })));
const AdminPricing = lazy(() => import("./pages/admin/Pricing").then(m => ({ default: m.AdminPricing })));
const AdminUsers = lazy(() => import("./pages/admin/Users").then(m => ({ default: m.AdminUsers })));
const WabaHealth = lazy(() => import("./pages/admin/WabaHealth").then(m => ({ default: m.WabaHealth })));
const AdminCredits = lazy(() => import("./pages/admin/Credits").then(m => ({ default: m.AdminCredits })));
const AuditLog = lazy(() => import("./pages/admin/AuditLog").then(m => ({ default: m.AuditLog })));
const BillingInvoices = lazy(() => import("./pages/admin/BillingInvoices").then(m => ({ default: m.BillingInvoices })));
// External billing — PhotonX + Maya (independent of wenext subscription billing)
const PhotonxDashboard = lazy(() => import("./pages/admin/photonx/PhotonxDashboard"));
const PhotonxClients = lazy(() => import("./pages/admin/photonx/PhotonxClients"));
const PhotonxProformas = lazy(() => import("./pages/admin/photonx/PhotonxProformas"));
const PhotonxInvoices = lazy(() => import("./pages/admin/photonx/PhotonxInvoices"));
const PhotonxInvoiceDetail = lazy(() => import("./pages/admin/photonx/PhotonxInvoiceDetail"));
const MayaDashboard = lazy(() => import("./pages/admin/maya/MayaDashboard"));
const MayaClients = lazy(() => import("./pages/admin/maya/MayaClients"));
const MayaProformas = lazy(() => import("./pages/admin/maya/MayaProformas"));
const MayaInvoices = lazy(() => import("./pages/admin/maya/MayaInvoices"));
const MayaInvoiceDetail = lazy(() => import("./pages/admin/maya/MayaInvoiceDetail"));
const OrgAuditLog = lazy(() => import("./pages/OrgAuditLog").then(m => ({ default: m.OrgAuditLog })));
const RevenueAnalytics = lazy(() => import("./pages/admin/RevenueAnalytics").then(m => ({ default: m.RevenueAnalytics })));
const ClientHealth = lazy(() => import("./pages/admin/ClientHealth").then(m => ({ default: m.ClientHealth })));
const AdminNotifications = lazy(() => import("./pages/admin/Notifications").then(m => ({ default: m.AdminNotifications })));
const FeatureFlags = lazy(() => import("./pages/admin/FeatureFlags").then(m => ({ default: m.FeatureFlags })));
const AdminSecurity = lazy(() => import("./pages/admin/Security").then(m => ({ default: m.AdminSecurity })));
const PlatformOverview = lazy(() => import("./pages/admin/PlatformOverview").then(m => ({ default: m.PlatformOverview })));
const CommerceMonitor = lazy(() => import("./pages/admin/CommerceMonitor").then(m => ({ default: m.CommerceMonitor })));
const SocialMediaMonitor = lazy(() => import("./pages/admin/SocialMediaMonitor").then(m => ({ default: m.SocialMediaMonitor })));
const CommunicationCenter = lazy(() => import("./pages/admin/CommunicationCenter").then(m => ({ default: m.CommunicationCenter })));
// Agency pages — lazy loaded
const AgencyDashboard = lazy(() => import("./pages/agency/Dashboard"));
const AgencyOrganizations = lazy(() => import("./pages/agency/Organizations"));
const AgencyCompensations = lazy(() => import("./pages/agency/Compensations"));
const AgencySettings = lazy(() => import("./pages/agency/AgencySettings"));
// Other heavy pages — lazy loaded
const CustomAPIPage = lazy(() => import("./pages/CustomAPI"));
const LiveChat = lazy(() => import("./pages/LiveChat"));
const ManageAiAgents = lazy(() => import("./components/Ai/ManageAiAgents"));
const AiAgentDetail = lazy(() => import("./components/Ai/AiAgentDetail"));
const Chatbot = lazy(() => import("./components/Ai/Chatbot"));
const Scheduling = lazy(() => import("./pages/Scheduling"));
// Public booking routes — not lazy (needed for external users)
import BookEvent from "./pages/BookEvent";
import BookingSuccess from "./pages/BookingSuccess";
import CancelBooking from "./pages/CancelBooking";
import RescheduleBooking from "./pages/RescheduleBooking";
const Channels = lazy(() => import("./components/live-chat/Channels"));
const FormDataStore = lazy(() => import("./pages/FormDataStore"));
const DataStoreManager = lazy(() => import("./pages/data-store/DataStoreManager"));
const DataStoreDetail = lazy(() => import("./pages/data-store/DataStoreDetail"));
const GoogleSheetsData = lazy(() => import("./pages/GoogleSheetsData").then(m => ({ default: m.GoogleSheetsData })));
const BoardDashboard = lazy(() => import("./pages/kanban/BoardDashboard").then(m => ({ default: m.BoardDashboard })));
const PlansAndSubscriptions = lazy(() => import("./pages/crm/PlansAndSubscriptions"));
const KanbanBoard = lazy(() => import("./pages/kanban/KanbanBoard").then(m => ({ default: m.KanbanBoard })));
const InstagramBroadcasts = lazy(() => import("./pages/marketing/InstagramBroadcasts"));
const InstagramAutomation = lazy(() => import("./pages/automations/InstagramAutomation"));
const SchedulingPosts = lazy(() => import("./pages/marketing/SchedulingPosts"));
const CreatePost = lazy(() => import("./pages/social-media/CreatePost"));
const MetaAds = lazy(() => import("./pages/ads-manager/MetaAds"));
const LeadAdsSync = lazy(() => import("./pages/ads-manager/LeadAdsSync"));
const WhatsAppAds = lazy(() => import("./pages/ads-manager/WhatsAppAds"));
const SchedulingOverview = lazy(() => import("./pages/scheduling/Overview"));
const Calendly = lazy(() => import("./pages/scheduling/Calendly"));
const TimeSlots = lazy(() => import("./pages/scheduling/TimeSlots"));
const MeetingLinks = lazy(() => import("./pages/scheduling/MeetingLinks"));
const SchedulingContacts = lazy(() => import("./pages/scheduling/Contacts"));
const WhatsAppReminders = lazy(() => import("./pages/WhatsAppReminders"));
const SchedulingIntegrations = lazy(() => import("./pages/scheduling/SchedulingIntegrations"));
const Orders = lazy(() => import("./pages/online-store/Orders"));
const OrderDetail = lazy(() => import("./pages/online-store/OrderDetail"));
const Products = lazy(() => import("./pages/online-store/Products"));
const AbondedCarts = lazy(() => import("./pages/online-store/AbondedCarts"));
const Payments = lazy(() => import("./pages/online-store/Payments"));
const StoreReports = lazy(() => import("./pages/online-store/StoreReports"));
const Invoices = lazy(() => import("./pages/online-store/Invoices"));
const Leads = lazy(() => import("./pages/crm/Leads"));
const Customers = lazy(() => import("./pages/crm/Customers"));
const Segments = lazy(() => import("./pages/crm/Segments"));
const ChatHistory = lazy(() => import("./pages/crm/ChatHistory"));
const NotesAndTags = lazy(() => import("./pages/crm/NotesAndTags"));
const MessageReports = lazy(() => import("./pages/analytics/MessageReports"));
const CampaignPerformance = lazy(() => import("./pages/analytics/CampaignPerformance"));
const AdsResults = lazy(() => import("./pages/analytics/AdsResults"));
const SalesAndConversions = lazy(() => import("./pages/analytics/SalesAndConversions"));
const HowToUse = lazy(() => import("./pages/help/HowToUse"));
const WhatsNew = lazy(() => import("./pages/help/WhatsNew"));
const HelpArticles = lazy(() => import("./pages/help/HelpArticles"));
const RaiseTickets = lazy(() => import("./pages/help/RaiseTickets"));
const ContactSupport = lazy(() => import("./pages/help/ContactSupport"));
// Social Accounts moved into /home/integrations as its "Social accounts"
// tab; this path now only redirects (see SocialAccountsRedirect).
const SocialAccountsRedirect = lazy(() => import("./pages/social-media/SocialAccountsRedirect"));
const SocialInbox = lazy(() => import("./pages/social-media/SocialInbox"));
const GenUIComponentsPage = lazy(() => import("./pages/admin/GenUIComponents").then(m => ({ default: m.GenUIComponents })));
const InstagramAccounts = lazy(() => import("./pages/social-media/InstagramAccounts"));
const YouTubeChannels = lazy(() => import("./pages/social-media/YouTubeChannels"));
const YouTubeComments = lazy(() => import("./pages/social-media/YouTubeComments"));
const YouTubeInsights = lazy(() => import("./pages/social-media/YouTubeInsights"));
const YouTubeAISettings = lazy(() => import("./pages/social-media/YouTubeAISettings"));
// Callback pages — not lazy (external redirects)
import InstagramCallback from "./pages/social-media/InstagramCallback";
import YouTubeCallback from "./pages/social-media/YouTubeCallback";
import FacebookCallback from "./pages/social-media/FacebookCallback";
import LinkedInCallback from "./pages/social-media/LinkedInCallback";
import TikTokCallback from "./pages/social-media/TikTokCallback";
const FacebookAccounts = lazy(() => import("./pages/social-media/FacebookAccounts"));
const TikTokAccounts = lazy(() => import("./pages/social-media/TikTokAccounts"));
const LinkedInAccounts = lazy(() => import("./pages/social-media/LinkedInAccounts"));
const LinkedInSelectAccount = lazy(() => import("./pages/social-media/linkedin/LinkedInSelectAccount"));
const AutomationFlows = lazy(() => import("./pages/social-media/instagram/AutomationFlows"));
const AutomationLeads = lazy(() => import("./pages/social-media/instagram/AutomationLeads"));
const AutomationLogs = lazy(() => import("./pages/social-media/instagram/AutomationLogs"));
const AutomationFlowBuilder = lazy(() => import("./pages/social-media/instagram/AutomationFlowBuilder"));
const CreateAutomationFlow = lazy(() => import("./pages/social-media/instagram/CreateAutomationFlow"));
const InstagramAutomationSelector = lazy(() => import("./pages/social-media/instagram/InstagramAutomationSelector"));
// InstagramPosts is no longer routed standalone (see the `instagram/posts`
// redirect below) — it is superseded by the Instagram tab inside
// SocialMediaPostsManager, which is now an independent implementation (not a
// wrapper around this component).
const InstagramInbox = lazy(() => import("./pages/social-media/instagram/InstagramInbox"));
const InstagramInsights = lazy(() => import("./pages/social-media/instagram/InstagramInsights"));
const InstagramOrders = lazy(() => import("./pages/social-media/instagram/InstagramOrders"));
// FacebookPosts is no longer routed standalone (see the `facebook/posts`
// redirect below) — same reasoning as InstagramPosts above.
// LinkedInPosts is no longer routed standalone (see the `linkedin/posts` redirect
// below) — it is rendered as the LinkedIn tab inside SocialMediaPostsManager.
const SocialMediaPostsManager = lazy(() => import("./pages/social-media/SocialMediaPostsManager"));
const Home = lazy(() => import("./pages/Home"));
const WhatsAppAiAgent = lazy(() => import("./components/WhatsAppAiAgent"));
const WhatsAppPayments = lazy(() => import("./pages/WhatsAppPayments"));
const GoogleAds = lazy(() => import("./pages/GoogleAds"));
const WhatsAppPaymentSettings = lazy(() => import("./pages/WhatsAppPaymentSettings"));
const NewInbox = lazy(() => import("./pages/new-inbox/NewInbox"));
const CallLogs = lazy(() => import("./pages/CallLogs"));
const MetaAdsPage = lazy(() => import("./pages/Ads/MetaAdsPage"));
const AdsSetup = lazy(() => import("./pages/Ads/AdsSetup"));
const AdDetail = lazy(() => import("./pages/Ads/AdDetail"));
const AdCreate = lazy(() => import("./pages/Ads/AdCreate"));
const AdAttribution = lazy(() => import("./pages/Ads/AdAttribution"));
const MetaAdDetail = lazy(() => import("./pages/Ads/MetaAdDetail"));
const CampaignDetail = lazy(() => import("./pages/Ads/CampaignDetail"));
const AdSetDetail = lazy(() => import("./pages/Ads/AdSetDetail"));
const AiAdBuilder = lazy(() => import("./pages/Ads/AiAdBuilder"));
const MetaLeads = lazy(() => import("./pages/Ads/MetaLeads"));
// Meta Ads A/B Testing (Experiments) — additive, does not touch AdCreate/AdsManager routing.
const Experiments = lazy(() => import("./pages/Ads/Experiments"));
const ExperimentDetail = lazy(() => import("./pages/Ads/ExperimentDetail"));

// LinkedIn Ads
const LinkedInAdsManager = lazy(() => import("./pages/linkedin-ads/LinkedInAdsManager"));
const LinkedInAdsSetup = lazy(() => import("./pages/linkedin-ads/LinkedInAdsSetup"));
const LinkedInAdCreate = lazy(() => import("./pages/linkedin-ads/LinkedInAdCreate"));
const LinkedInCampaignDetail = lazy(() => import("./pages/linkedin-ads/LinkedInCampaignDetail"));
const LinkedInCampaignEdit = lazy(() => import("./pages/linkedin-ads/LinkedInCampaignEdit"));
const LinkedInAdsAnalytics = lazy(() => import("./pages/linkedin-ads/LinkedInAdsAnalytics"));
const LinkedInAdLibrary = lazy(() => import("./pages/linkedin-ads/LinkedInAdLibrary"));
const LinkedInEvents = lazy(() => import("./pages/linkedin-ads/LinkedInEvents"));
const LinkedInVerified = lazy(() => import("./pages/linkedin-ads/LinkedInVerified"));

const PetpoojaDashboard = lazy(() => import("./pages/petpooja/PetpoojaDashboard"));
const PetpoojaOrders = lazy(() => import("./pages/petpooja/PetpoojaOrders"));
const TikTokAdsDashboard = lazy(() => import("./pages/tiktok-ads/TikTokAdsDashboard"));

// Onboarding V2 — the first-run journey. The preflight renders in its own bare
// shell (own logo header, no sidebar); the dashboard renders inside AppShell
// with the full provider stack, because its connection actions use the
// business-account context and React Query cache.
// FirstRunOnboardingGate sends a first-time organization here; reaching the
// dashboard is what records onboarding as complete.
const OnboardingV2Preflight = lazy(() => import("./pages/onboarding-v2/OnboardingV2Preflight"));
const OnboardingV2Dashboard = lazy(() => import("./pages/onboarding-v2/OnboardingV2Dashboard"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors).
        //
        // 🔴 This read `error.status` only, but ApiError — what every apiClient
        // throw site raises — carries the status as `statusCode` and NEVER sets
        // `status`. So `'status' in error` was always false, the 4xx skip was
        // dead code, and EVERY failed query retried 3 times app-wide. Read both:
        // `statusCode` is the real field, `status` is kept for any raw fetch
        // rejection that does set it.
        const status = (error as { statusCode?: unknown; status?: unknown } | null)?.statusCode
          ?? (error as { status?: unknown } | null)?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  },
});

// ── First-run onboarding gate ────────────────────────────────────────────────
// A first-time organization is sent to the PREFLIGHT (/onboarding) — the
// questions — and the preflight hands over to the setup plan
// (/onboarding/dashboard) when they finish or skip. The answers are what turn the
// plan's generic checklist into one naming their goals and channels, so asking
// first is what makes the plan worth landing on.
//
// The OTHER redirect below — "signed up but never connected a channel", which
// fires on every login — goes straight to the plan instead. Those users are past
// first run and have answered these questions already.
//
// Replaces the V1 survey modal, whose questions produced a record nobody acted
// on.
//
// The detection logic below is UNCHANGED from the survey gate and is
// deliberately conservative — every ambiguous answer (network failure, 5xx,
// 401 mid-refresh) resolves to "do nothing" rather than "onboard them again",
// because a false positive now redirects a working user out of their workspace
// instead of merely showing a dismissable dialog.
//
// Loop safety, three layers:
//  1. The local mirror is checked before any request, so a failed
//     POST /onboarding/complete can never trap anyone.
//  2. Leaving the preflight marks onboarding complete for the org — including
//     via Skip, which every question step offers — and so does reaching the
//     setup plan. So the questions can be walked out of, never only through.
//  3. This gate only mounts under /home/*, and the V2 routes live outside it,
//     so the destination can never re-trigger the redirect.
function FirstRunOnboardingGate() {
  const { user } = useAuth();
  const location = useLocation();
  const { accounts, isLoading: accountsLoading } = useBusinessAccountContext();
  // Three states, not two. `unknown` is what every ambiguous answer resolves to
  // — a failed /onboarding/status, a subscription call that never came back, a
  // local mirror belonging to some other workspace. It must stay distinct from
  // `completed`, because only `completed` may send someone to the setup plan,
  // and reaching the plan records onboarding as done for their organization.
  const [verdict, setVerdict] = useState<'unknown' | 'completed' | 'not-completed'>('unknown');
  const [checking, setChecking] = useState(true);
  const organizationId = (user as any)?.organization_id ?? null;

  // Only a new customer setting up their own workspace — never a WeNext platform
  // admin inspecting a client, never an agency user, never an invited member.
  // See `isFirstRunOnboardingAudience` for why each is excluded; it is unit
  // tested across the whole role matrix.
  const canOnboard = isFirstRunOnboardingAudience(user);

  // The check must run exactly once per mount, but not until `user` exists.
  // AuthProvider hydrates the session asynchronously, so the first render can
  // have `user === null` — running then would evaluate `canOnboard` as false and
  // silently skip onboarding for the whole session.
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (!user || hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    // Always check backend — it is the source of truth.
    // localStorage is only used as a fallback when the API is unreachable.
    const checkOnboarding = async () => {
      // Hoisted so the outer catch can still fail-open a brand-new signup when
      // the network dies mid-check.
      const freshSignup = isNewSignupPending();
      try {
        // Not this user's flow to run.
        if (!canOnboard) {
          setChecking(false);
          return;
        }

        const token = localStorage.getItem('auth_token');
        if (!token) {
          setChecking(false);
          return;
        }

        // Local mirror first, before any request. This is the loop guard: once
        // the user has been through the flow they are never redirected again,
        // even if POST /onboarding/complete failed and the server still says
        // false. The pending marker gets the server record caught up.
        // Scoped to THIS organization — a mirror left by a different workspace
        // signed into the same browser is a miss, and falls through to the
        // server check below. See ONBOARDING_COMPLETED_KEY.
        if (isOnboardingCompletedLocally(organizationId)) {
          setVerdict('completed');
          setChecking(false);
          if (isOnboardingCompletePending()) void markOnboardingComplete(organizationId);
          return;
        }

        const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

        // A session that just created its account gets to skip the whole
        // "was this workspace already set up?" heuristic below. It cannot tell us
        // anything true about a workspace that is seconds old, and every way it
        // can fail — a 5xx, a dropped column, an offline blip — resolves to
        // "silently skip onboarding for this entire session", which is exactly the
        // reported bug. `/onboarding/status` is still consulted, so a fresh signup
        // whose org somehow reads completed is not redirected either.
        // (freshSignup already computed above.)

        // Check subscription status first — a PAID subscription (even expired or
        // cancelled) means somebody already set this workspace up, so don't drag
        // a returning user back through onboarding.
        //
        // The signup trial does NOT count: `AuthService.register` creates a
        // 15-day trial for every new account, so "has a subscription row" was
        // true for brand-new users too, and this branch skipped onboarding for
        // exactly the people it exists to serve. See
        // `subscriptionProvesPriorSetup`.
        //
        // Reads from React Query's cache to avoid a duplicate HTTP call: the
        // shared useCurrentSubscription hook (mounted by AppShell, AppSidebar,
        // TopNavigation, etc.) has usually already populated this entry by
        // the time the gate runs. If the cache is cold — e.g. this gate is
        // the very first thing to render on a fresh login — we fall back to
        // `fetchQuery`, which fires one request and seeds the cache for all
        // the other consumers. Either way, only ONE `/subscriptions/current`
        // request hits the wire per page load.
        if (!freshSignup) try {
          const CURRENT_SUBSCRIPTION_KEY = ['current-subscription'] as const;
          const cached = queryClient.getQueryData(CURRENT_SUBSCRIPTION_KEY) as any;
          let subscription = cached ?? null;
          if (!subscription) {
            subscription = await queryClient.fetchQuery({
              queryKey: CURRENT_SUBSCRIPTION_KEY,
              queryFn: async () => {
                const r = await fetch(`${baseUrl}/subscriptions/current`, {
                  headers: { 'Authorization': `Bearer ${token}` },
                });
                // IMPORTANT: throw on transient failures (401 mid-token-refresh,
                // 5xx, network blip) instead of returning null. Returning null
                // would (a) seed the shared `current-subscription` cache with
                // null and (b) make this gate falsely conclude "no subscription
                // → show onboarding survey" for a fully-onboarded user. Throwing
                // keeps the cache untouched and bails out of the "no sub"
                // branch via the outer catch.
                if (!r.ok) throw new Error(`subscriptions/current ${r.status}`);
                const j = await r.json();
                return j?.data ?? null;
              },
            });
          }
          if (subscriptionProvesPriorSetup(subscription)) {
            cacheOnboardingCompleted(organizationId);
            setVerdict('completed');
            setChecking(false);
            return;
          }
        } catch {
          // Subscription check failed transiently. We can't tell whether the
          // user has a sub, so we MUST NOT fall through to the onboarding
          // status check — that path defaults to showing the survey on any
          // failure, which is exactly the false-redirect bug. Trust localStorage
          // if it says onboarding is done; otherwise wait for the next reload
          // when the network has likely recovered.
          if (isOnboardingCompletedLocally(organizationId)) {
            setVerdict('completed');
            setChecking(false);
            return;
          }
          // No local hint and no network — the verdict stays `unknown`, which
          // is NOT the same as "already onboarded". See the destination rule.
          setChecking(false);
          return;
        }

        const response = await fetch(`${baseUrl}/onboarding/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.onboarding_completed) {
            cacheOnboardingCompleted(organizationId);
            setVerdict('completed');
            setChecking(false);
            return;
          }
          // Backend says explicitly "not completed" — only now onboard them.
          setVerdict('not-completed');
          setChecking(false);
          return;
        }

        // Non-OK response from /onboarding/status (401 race, 5xx). For a
        // returning user, prefer a false negative (stay put). For a brand-new
        // signup, prefer the questions — silently dropping them on Overview is
        // the reported bug, and they cannot have completed onboarding yet.
        if (freshSignup) {
          setVerdict('not-completed');
          setChecking(false);
          return;
        }
        setChecking(false);
      } catch (error) {
        // Network exception (DNS, offline). Fresh signups still get the
        // questions; for everyone else the verdict stays `unknown`.
        if (freshSignup) setVerdict('not-completed');
        setChecking(false);
      }
    };

    void checkOnboarding();
    // `canOnboard` is derived from `user` and the ref makes this run once, so
    // depending on `user` alone is both sufficient and stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── No connected business account → the setup plan ────────────────────────
  // On EVERY login, not just the first one. A workspace with no business account
  // cannot send a message, run an automation or receive a conversation, so the
  // Overview has nothing to show it — the setup plan is the only page with
  // anything to do on it. "Signed up but never finished connecting" is a state
  // people sit in for weeks, and the first-run check below does not catch them:
  // it fires on `onboarding_completed`, which they already are.
  //
  // Four conditions, each load-bearing:
  //  - `canOnboard` — same audience as the first-run redirect. An invited agent
  //    or a WeNext admin inspecting a client must not be dumped on this plan.
  //  - `!checking` — the first-run verdict has to settle first, because the two
  //    reasons no longer share a destination. On a brand-new signup BOTH are
  //    true, and the accounts query usually resolves before /onboarding/status
  //    does; without this the no-account reason wins that race and lands a
  //    first-time user on the plan, skipping the questions entirely.
  //  - `isHomeRoot` — only the Overview. Every other page keeps working, so a
  //    deep link still resolves and the user is never boxed in; without this,
  //    /home/campaigns and the rest would bounce too.
  //  - a DEFINITE empty list — `accounts` is null while the query is pending or
  //    disabled (and when no provider is mounted at all), and reading that as
  //    "no account" would redirect users who have one. That is the bug
  //    BusinessAccountContext's own isLoading comment describes.
  //  - NOT a pending new signup — brand-new accounts always have zero BAs, so
  //    without this exclusion the no-account branch steals them to the plan
  //    whenever firstRunRedirect did not win (hasCheckedRef already fired,
  //    status check deferred, etc.). They belong on the questions first.
  const isHomeRoot = location.pathname.replace(/\/+$/, '') === '/home';
  const hasNoBusinessAccount =
    !accountsLoading && Array.isArray(accounts) && accounts.length === 0;
  // LOOP GUARD. Both onboarding routes are wrapped in OnboardingFirstRunGuard,
  // which bounces anyone holding a connected account back to /home — and /home
  // is where this gate lives. So a redirect toward the questions for such a
  // user would ping-pong forever. This is deliberately the DEFINITE non-empty
  // test (not `!hasNoBusinessAccount`): an unsettled list must not suppress a
  // genuine first-run redirect, and the guard fails open on the same unknown,
  // so the two always agree.
  const hasConnectedAccount =
    !accountsLoading && Array.isArray(accounts) && accounts.length > 0;
  const pendingSignup = isNewSignupPending();
  const noAccountRedirect =
    canOnboard &&
    !checking &&
    isHomeRoot &&
    hasNoBusinessAccount &&
    !pendingSignup;
  // Belt-and-suspenders: a pending signup that somehow sits on /home (Auth
  // raced ahead of PhoneAuthFlow's navigate, gate check deferred) still goes
  // to the questions — never the plan.
  const pendingSignupRedirect =
    canOnboard && !checking && isHomeRoot && pendingSignup && !hasConnectedAccount;

  // Nothing is rendered either way — this gate only ever navigates.
  const firstRunRedirect = verdict === 'not-completed' && !checking && !hasConnectedAccount;
  if (!firstRunRedirect && !pendingSignupRedirect && !noAccountRedirect) return null;

  // ── Which page? The plan requires PROOF; everything else gets the questions ─
  //
  // The two destinations are not symmetric. Reaching the setup plan calls
  // `markOnboardingComplete()` on mount, so it writes `onboarding_completed`
  // for the organization — irreversibly, from the user's point of view. The
  // questions write nothing until the user leaves them, and every step offers
  // Skip. So an unproven guess costs nothing in one direction and costs a
  // customer their entire first-run experience in the other.
  //
  // Hence: only a CONFIRMED completion may send someone to the plan. A verdict
  // of `unknown` — /onboarding/status 5xx'd, the subscription call died, the
  // local mirror belongs to another workspace — lands on the questions.
  //
  // Before this rule the no-account branch fired on `unknown` too, which is how
  // a brand-new account reached /onboarding/dashboard, silently recorded itself
  // as onboarded, and could never be shown the questions again.
  const confirmedOnboarded = verdict === 'completed';
  const goToQuestions =
    firstRunRedirect || pendingSignupRedirect || !confirmedOnboarded;

  // The two reasons lead to DIFFERENT pages, and first run / pending signup win.
  //
  //  - First run / pending signup → the preflight questions (/onboarding). This
  //    is someone's very first minute in the product, and the answers are what
  //    personalize the plan that follows. Every step offers Skip, which records
  //    completion and hands over to the plan, so nobody is trapped in the questions.
  //  - Signed up but never connected a channel, AND confirmed onboarded → straight
  //    to the plan (/onboarding/dashboard). This one fires on EVERY login, and they
  //    are past first run by definition — re-asking the same questions each time
  //    would be a nag, while the plan is the page with something left to do on it.
  return (
    <Navigate
      to={goToQuestions ? ONBOARDING_V2_PREFLIGHT_PATH : ONBOARDING_V2_DASHBOARD_PATH}
      replace
    />
  );
}

const OverviewRouteWrapper = () => {
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  // Agency users have no tenant workspace (organization_id is null) — the
  // /home Overview (Dashboard) is the Meta-connect onboarding for regular
  // tenants and is meaningless for them. Send them to the agency portal.
  // This is the reverse of AgencyRoute (which bounces non-agency users OUT of
  // /home/agency/*) and also catches an agency user who lands on /home directly
  // (bookmark, stale post-login redirect, etc.).
  if (user?.is_agency) {
    return <Navigate to="/home/agency/dashboard" replace />;
  }
  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(user as any).is_super_admin;
  const isOwner = !!(user as any).is_organization_owner;
  const effectiveRole = (roleRaw === 'ADMIN' || isSuperAdmin || isOwner) ? 'ADMIN' : (roleRaw === 'AGENT' ? 'AGENT' : 'USER');
  if (effectiveRole === 'AGENT') {
    return <Navigate to="/home/agent" replace />;
  }
  return <Dashboard />;
};

const App = () => {
  // Initialize Microsoft Clarity on app mount
  useEffect(() => {
    initializeClarity();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <WidgetConfigProvider>
            <Toaster />
            <GlobalSyncAnimator />
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <NavigationProvider>
                <Routes>
                  {/* Root route redirects to auth */}
                  <Route path="/" element={<Navigate to="/auth" replace />} />
                  <Route path="/privacy-policy" element={<LandingPrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<LandingTermsOfService />} />

                  {/* Authentication routes */}
                  <Route path="/auth" element={<Auth />} />
                  {/* Public device-routing landing for the mobile-app QR (iOS→App Store, Android→Play). */}
                  <Route path="/get-app" element={<GetApp />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/agency/login" element={<AgencyLogin />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/wenext/callback" element={<MetaCallback />} />
                  <Route path="/meta-callback" element={<MetaCallback />} />
                  <Route path="/meta-ads-callback" element={<MetaCallback />} />
                  <Route path="/shopify-callback" element={<ShopifyCallback />} />
                  <Route path="/razorpay/callback" element={<RazorpayCallback />} />
                  <Route path="/wa-connect" element={<WaConnect />} />
                  <Route path="/payment/return" element={<PaymentReturn />} />
                  <Route path="/instagram-callback" element={<InstagramCallback />} />
                  <Route path="/youtube-callback" element={<YouTubeCallback />} />
                  <Route path="/facebook-callback" element={<FacebookCallback />} />
                  <Route path="/fcebook-callback" element={<FacebookCallback />} />
                  <Route path="/linkedin-callback" element={<LinkedInCallback />} />
                  <Route path="/tiktok-callback" element={<TikTokCallback />} />

                  {/* Public booking routes */}
                  <Route path="/book/:orgSlug/:slug" element={<BookEvent />} />
                  <Route path="/book/:slug" element={<BookEvent />} /> {/* Backward compatibility */}
                  <Route path="/booking-success/:bookingId" element={<BookingSuccess />} />
                  <Route path="/booking/:bookingId/cancel" element={<CancelBooking />} />
                  <Route path="/booking/:bookingId/reschedule" element={<RescheduleBooking />} />

                  {/* Legacy route redirects for production compatibility */}
                  <Route path="/inbox" element={<Navigate to="/home/inbox" replace />} />
                  <Route path="/new-inbox" element={<Navigate to="/home/new-inbox" replace />} />
                  <Route path="/contacts" element={<Navigate to="/home/contacts" replace />} />
                  <Route path="/templates" element={<Navigate to="/home/templates" replace />} />
                  <Route path="/campaigns" element={<Navigate to="/home/campaigns" replace />} />
                  <Route path="/analytics" element={<Navigate to="/home/analytics" replace />} />
                  <Route path="/commerce" element={<Navigate to="/home/commerce/products" replace />} />
                  <Route path="/team-chat" element={<Navigate to="/home/team-chat" replace />} />
                  <Route path="/agents" element={<Navigate to="/home/agents" replace />} />
                  <Route path="/automation" element={<Navigate to="/home/automation" replace />} />
                  <Route path="/automation/*" element={<Navigate to="/home/automation" replace />} />
                  <Route path="/settings" element={<Navigate to="/home/settings" replace />} />
                  <Route path="/integrations" element={<Navigate to="/home/integrations" replace />} />
                  <Route path="/add-customer" element={<Navigate to="/home/add-customer" replace />} />
                  <Route path="/team-demo" element={<TeamDemo />} />

                  {/* Admin routes (super admin only) */}
                  <Route path="/admin/*" element={
                    <SuperAdminRoute>
                      <AppShell>
                        <Suspense fallback={<div className="flex items-center justify-center h-full w-full py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
                        <Routes>
                          <Route path="dashboard" element={<AdminDashboard />} />
                          <Route path="clients" element={<AdminClients />} />
                          <Route path="internal-accounts" element={<AdminInternalAccounts />} />
                          <Route path="clients/:id" element={<ClientDetails />} />
                          <Route path="clients/:id/plan" element={<OrganizationPlanManagement />} />
                          <Route path="users" element={<AdminUsers />} />
                          <Route path="templates" element={<Navigate to="/admin/library" replace />} />
                          <Route path="library" element={<AdminLibrary />} />
                          <Route path="library/whatsapp-flow/builder" element={<WhatsAppFlowBuilder />} />
                          <Route path="library/flow/builder" element={<FlowBuilder />} />
                          <Route path="subscriptions" element={<AdminSubscriptions />} />
                          <Route path="subscription-plans" element={<AdminSubscriptions initialTab="plans" />} />
                          <Route path="pricing" element={<AdminPricing />} />
                          <Route path="agencies" element={<AdminAgencies />} />
                          <Route path="integrations" element={<AdminIntegrations />} />
                          <Route path="settings" element={<AdminSettings />} />
                          <Route path="waba-health" element={<WabaHealth />} />
                          <Route path="credits" element={<AdminCredits />} />
                          <Route path="audit-log" element={<AuditLog />} />
                          <Route path="billing/invoices" element={<BillingInvoices />} />
                          {/* External billing — PhotonX + Maya (independent of wenext) */}
                          <Route path="photonx" element={<PhotonxDashboard />} />
                          <Route path="photonx/clients" element={<PhotonxClients />} />
                          <Route path="photonx/proformas" element={<PhotonxProformas />} />
                          <Route path="photonx/invoices" element={<PhotonxInvoices />} />
                          <Route path="photonx/invoices/:id" element={<PhotonxInvoiceDetail />} />
                          <Route path="maya" element={<MayaDashboard />} />
                          <Route path="maya/clients" element={<MayaClients />} />
                          <Route path="maya/proformas" element={<MayaProformas />} />
                          <Route path="maya/invoices" element={<MayaInvoices />} />
                          <Route path="maya/invoices/:id" element={<MayaInvoiceDetail />} />
                          <Route path="revenue-analytics" element={<RevenueAnalytics />} />
                          <Route path="client-health" element={<ClientHealth />} />
                          <Route path="notifications" element={<AdminNotifications />} />
                          <Route path="feature-flags" element={<FeatureFlags />} />
                          <Route path="security" element={<AdminSecurity />} />
                          <Route path="platform-overview" element={<PlatformOverview />} />
                          <Route path="commerce" element={<CommerceMonitor />} />
                          <Route path="social-media" element={<SocialMediaMonitor />} />
                          <Route path="communication-center" element={<CommunicationCenter />} />
                          <Route path="" element={<Navigate to="/admin/dashboard" replace />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        </Suspense>
                      </AppShell>
                    </SuperAdminRoute>
                  } />

                  {/* App routes (authentication required) */}
                  <Route path="/home/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/home/terms-of-service" element={<TermsOfService />} />

                  {/* Onboarding — preflight (questions only, bare shell). It
                      never opens Meta; every connection lives on the dashboard
                      route below.

                      The guard reads the accounts list from useBusinessAccounts
                      directly, NOT from BusinessAccountProvider — this route's
                      bare shell is deliberate and test-locked, and the provider
                      would drag its full-screen "Loading Dashboard" state onto
                      a first-run question screen. */}
                  <Route path="/onboarding" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <OnboardingFirstRunGuard>
                          <Suspense fallback={<div className="flex items-center justify-center h-screen w-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
                            <OnboardingV2Preflight />
                          </Suspense>
                        </OnboardingFirstRunGuard>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />

                  {/* Onboarding — setup plan. Inside AppShell + the full
                      provider stack: the Meta modal writes to the
                      business-account context and invalidates the React Query
                      cache on connect.

                      OnboardingFirstRunGuard sits OUTSIDE AppShell (and outside
                      the provider stack) so a user who is past first run never
                      mounts the shell or the plan at all — no flash of a setup
                      checklist they have already completed. This is the only
                      thing standing between a bookmark / typed URL / back
                      button and a stale first-run page. */}
                  <Route path="/onboarding/dashboard" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <OnboardingFirstRunGuard>
                        <BusinessAccountProvider>
                          <ChatProvider>
                            <CallProvider>
                              <UnreadMessagesProvider>
                                <MessageStatusProvider>
                                  <NotificationProvider>
                                    <IncomingCallModal />
                                    <ActiveCallWindow />
                                    <AppShell>
                                      <Suspense fallback={<div className="flex items-center justify-center h-full w-full py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
                                        <OnboardingV2Dashboard />
                                      </Suspense>
                                    </AppShell>
                                  </NotificationProvider>
                                </MessageStatusProvider>
                              </UnreadMessagesProvider>
                            </CallProvider>
                          </ChatProvider>
                        </BusinessAccountProvider>
                        </OnboardingFirstRunGuard>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />

                  {/* Onboarding — legacy /preview/onboarding-v2 URLs. The gate
                      used to send users here, so an open tab, a bookmark or a
                      restored session must not dead-end on NotFound halfway
                      through first-run setup. Dashboard first: React Router
                      matches the more specific path regardless of order, but
                      reading order should match matching order. */}
                  <Route
                    path={ONBOARDING_V2_LEGACY_DASHBOARD_PATH}
                    element={<Navigate to={ONBOARDING_V2_DASHBOARD_PATH} replace />}
                  />
                  <Route
                    path={ONBOARDING_V2_LEGACY_PREFLIGHT_PATH}
                    element={<Navigate to={ONBOARDING_V2_PREFLIGHT_PATH} replace />}
                  />

                  {/* Agency routes - separate shell and layout */}
                  <Route path="/home/agency/*" element={
                    <ProtectedRoute>
                      <AgencyRoute>
                        <BusinessAccountProvider>
                          <ChatProvider>
                            <CallProvider>
                            <UnreadMessagesProvider>
                              <MessageStatusProvider>
                                <NotificationProvider>
                                  <IncomingCallModal />
                                  <ActiveCallWindow />
                                  <AppShell>
                                    <Suspense fallback={<div className="flex items-center justify-center h-full w-full py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
                                    <Routes>
                                      <Route index element={<Navigate to="dashboard" replace />} />
                                      <Route path="dashboard" element={<AgencyDashboard />} />
                                      <Route path="organizations" element={<AgencyOrganizations />} />
                                      <Route path="compensations" element={<AgencyCompensations />} />
                                      <Route path="settings" element={<AgencySettings />} />
                                      <Route path="*" element={<Navigate to="dashboard" replace />} />
                                    </Routes>
                                    </Suspense>
                                  </AppShell>
                                </NotificationProvider>
                              </MessageStatusProvider>
                            </UnreadMessagesProvider>
                            </CallProvider>
                          </ChatProvider>
                        </BusinessAccountProvider>
                      </AgencyRoute>
                    </ProtectedRoute>
                  } />

                  {/* Regular app routes (organizations/clients) */}
                  <Route path="/home/*" element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <BusinessAccountProvider>
                          <ChatProvider>
                            <CallProvider>
                            <UnreadMessagesProvider>
                              <MessageStatusProvider>
                                <NotificationProvider>
                                  <IncomingCallModal />
                                  <ActiveCallWindow />
                                  <AppShell>
                                    <FirstRunOnboardingGate />
                                    <Suspense fallback={<div className="flex items-center justify-center h-full w-full py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
                                    <Routes>
                                      <Route path="" element={<OverviewRouteWrapper />} />
                                      <Route path="main-home" element={<RoleRoute allowed={["ADMIN"]}><Home /></RoleRoute>} />
                                      <Route path="whatsapp-ai-agent" element={<RoleRoute allowed={["ADMIN"]}><WhatsAppAiAgent /></RoleRoute>} />
                                      <Route path="agent" element={<RoleRoute allowed={["AGENT"]}><AgentDashboard /></RoleRoute>} />
                                      <Route path="inbox" element={<InboxRedirect />} />
                                      <Route path="channels" element={<Navigate to="/home/channels/whatsapp" replace />} />
                                      <Route path="channels/:channel" element={<FeatureRoute featureKey="inbox"><ChannelChat /></FeatureRoute>} />
                                      <Route path="new-inbox" element={<FeatureRoute featureKey="inbox"><NewInbox /></FeatureRoute>} />
                                      <Route path="call-logs" element={<RoleRoute allowed={["ADMIN"]}><CallLogs /></RoleRoute>} />
                                      <Route path="contacts" element={<FeatureRoute featureKey="contacts"><RoleRoute allowed={["ADMIN", "USER"]}><Contacts /></RoleRoute></FeatureRoute>} />
                                      <Route path="tickets" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><Tickets /></RoleRoute>} />
                                      <Route path="tickets/statuses" element={<RoleRoute allowed={["ADMIN"]}><ManageTicketStatuses /></RoleRoute>} />
                                      <Route path="templates" element={<FeatureRoute featureKey="templates"><RoleRoute allowed={["ADMIN", "USER"]}><Templates /></RoleRoute></FeatureRoute>} />
                                      <Route path="campaigns" element={<FeatureRoute featureKey="campaigns"><RoleRoute allowed={["ADMIN", "USER"]}><Campaigns /></RoleRoute></FeatureRoute>} />
                                      {/* TEMPORARY preview routes — same guards as the real routes above, so
                                          access control behaves identically. Remove alongside the
                                          'new-campaigns' sidebar entry once cut over. */}
                                      <Route path="new-campaigns" element={<FeatureRoute featureKey="campaigns"><RoleRoute allowed={["ADMIN", "USER"]}><NewCampaigns /></RoleRoute></FeatureRoute>} />
                                      <Route path="new-templates" element={<FeatureRoute featureKey="templates"><RoleRoute allowed={["ADMIN", "USER"]}><NewTemplates /></RoleRoute></FeatureRoute>} />
                                      <Route path="analytics" element={<FeatureRoute featureKey="analytics"><RoleRoute allowed={["ADMIN"]}><Analytics /></RoleRoute></FeatureRoute>} />
                                      <Route path="commerce/products" element={<FeatureRoute featureKey="commerce"><RoleRoute allowed={["ADMIN"]}><Commerce /></RoleRoute></FeatureRoute>} />
                                      <Route path="commerce/dashboard" element={<FeatureRoute featureKey="commerce"><RoleRoute allowed={["ADMIN"]}><CommerceDashboardPage /></RoleRoute></FeatureRoute>} />
                                      <Route path="commerce/v2" element={<FeatureRoute featureKey="commerce"><RoleRoute allowed={["ADMIN"]}><CommerceV2 /></RoleRoute></FeatureRoute>} />
                                      <Route path="genui" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><GenUIWorkspace /></RoleRoute>} />
                                      <Route path="team-chat" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><TeamChat /></RoleRoute>} />
                                      <Route path="agents" element={<RoleRoute allowed={["ADMIN"]}><Agents /></RoleRoute>} />
                                      <Route path="flows" element={<FeatureRoute featureKey="whatsapp_flows"><RoleRoute allowed={["ADMIN"]}><WhatsAppFlows /></RoleRoute></FeatureRoute>} />
                                      <Route path="flows/builder" element={<FeatureRoute featureKey="whatsapp_flows"><RoleRoute allowed={["ADMIN"]}><WhatsAppFlowBuilder /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation" element={<FeatureRoute featureKey="automations"><RoleRoute allowed={["ADMIN"]}><Flows /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/flows" element={<FeatureRoute featureKey="automations"><RoleRoute allowed={["ADMIN"]}><Flows /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/flows/:id" element={<FeatureRoute featureKey="automations"><RoleRoute allowed={["ADMIN"]}><FlowBuilder /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/flows/:flowId/executions" element={<FeatureRoute featureKey="automations"><RoleRoute allowed={["ADMIN"]}><FlowExecutions /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/flows/:flowId/analytics" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramFlowAnalytics /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/runs" element={<FeatureRoute featureKey="automations"><RoleRoute allowed={["ADMIN"]}><Runs /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/instagram" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramFlowsListRedirect /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/instagram/:id" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramFlowBuilder /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/instagram/:id/analytics" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramAnalyticsLegacyRedirect /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/facebook" element={<FeatureRoute featureKey="facebook_automation" requireFeatureKey="facebook_posts"><RoleRoute allowed={["ADMIN"]}><FacebookFlowsListRedirect /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/facebook/new" element={<FeatureRoute featureKey="facebook_automation" requireFeatureKey="facebook_posts"><RoleRoute allowed={["ADMIN"]}><FacebookFlowBuilder /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/facebook/:id" element={<FeatureRoute featureKey="facebook_automation" requireFeatureKey="facebook_posts"><RoleRoute allowed={["ADMIN"]}><FacebookFlowBuilder /></RoleRoute></FeatureRoute>} />
                                      <Route path="automation/facebook/:id/analytics" element={<FeatureRoute featureKey="facebook_automation" requireFeatureKey="facebook_posts"><RoleRoute allowed={["ADMIN"]}><FacebookFlowAnalytics /></RoleRoute></FeatureRoute>} />
                                      {/* Voice agents page stays routed for the same reason as `live-chat`
                                          above: the sidebar points at the unified workspace per D1, but this
                                          page still owns Stop (`useStopVoiceAgent`) and the archived state,
                                          neither of which the workspace card exposes yet. Redirecting it
                                          removed the only way to stop a running agent, and made the voice
                                          cards' Configure action bounce straight back to the workspace.
                                          Folded into the merged editor in P1.4. Role list unchanged
                                          (ADMIN | USER | AGENT) — voice was never ADMIN-only. */}
                                      <Route path="voice-agents" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><VoiceAgents /></RoleRoute>} />
                                      <Route path="account-settings" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><AccountSettings /></RoleRoute>} />
                                      <Route path="settings" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><Settings /></RoleRoute>} />
                                      <Route path="audit-log" element={<RoleRoute allowed={["ADMIN"]}><OrgAuditLog /></RoleRoute>} />
                                      <Route path="billing" element={<RoleRoute allowed={["ADMIN"]}><Billing /></RoleRoute>} />
                                      <Route path="short-links" element={<RoleRoute allowed={["ADMIN"]}><ShortLinks /></RoleRoute>} />
                                      {/* Same ADMIN guard as the list — a deep link must not
                                          reach detail for a page the user cannot open.
                                          `isAdminPreferencePath` / `routeMatches` already
                                          cover sub-paths, so no permission-map entry. */}
                                      <Route path="short-links/:id" element={<RoleRoute allowed={["ADMIN"]}><ShortLinkDetail /></RoleRoute>} />
                                      <Route path="competitor-ads" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><CompetitorAds /></RoleRoute>} />
                                      {/* Hosts BOTH the integrations grid and the Social accounts
                                          tab, so either feature grants access. */}
                                      <Route path="integrations" element={<FeatureRoute featureKey="integrations" alsoAllowFeatureKey="social_media_posts"><RoleRoute allowed={["ADMIN"]}><Integrations /></RoleRoute></FeatureRoute>} />
                                      <Route path="shopify" element={<FeatureRoute featureKey="integrations"><RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="shopify"><Shopify /></IntegrationRoute></RoleRoute></FeatureRoute>} />
                                      <Route path="woocommerce" element={<FeatureRoute featureKey="integrations"><RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="woocommerce"><WooCommerce /></IntegrationRoute></RoleRoute></FeatureRoute>} />
                                      <Route path="razorpay" element={<FeatureRoute featureKey="integrations"><RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="razorpay"><Razorpay /></IntegrationRoute></RoleRoute></FeatureRoute>} />
                                      <Route path="cashfree" element={<FeatureRoute featureKey="integrations"><RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="cashfree"><Cashfree /></IntegrationRoute></RoleRoute></FeatureRoute>} />
                                      <Route path="payu" element={<FeatureRoute featureKey="integrations"><RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="payu"><Payu /></IntegrationRoute></RoleRoute></FeatureRoute>} />
                                      <Route path="uber-direct" element={<FeatureRoute featureKey="integrations"><RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="uber_direct"><UberDirect /></IntegrationRoute></RoleRoute></FeatureRoute>} />
                                      <Route path="google-ads" element={<FeatureRoute featureKey="google_ads"><RoleRoute allowed={["ADMIN"]}><GoogleAds /></RoleRoute></FeatureRoute>} />
                                      <Route path="website-builder" element={<RoleRoute allowed={["ADMIN"]}><WebsiteBuilderDashboard /></RoleRoute>} />
                                      <Route path="website-builder/sites/:siteId/editor/:pageId" element={<RoleRoute allowed={["ADMIN"]}><WebsiteBuilderEditor /></RoleRoute>} />
                                      <Route path="whatsapp-payments" element={<RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="whatsapp_payments"><WhatsAppPayments /></IntegrationRoute></RoleRoute>} />
                                      <Route path="whatsapp-payment-settings" element={<RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="whatsapp_payments"><WhatsAppPaymentSettings /></IntegrationRoute></RoleRoute>} />
                                      <Route path="google-sheets" element={<FeatureRoute featureKey="integrations"><RoleRoute allowed={["ADMIN"]}><GoogleSheets /></RoleRoute></FeatureRoute>} />
                                      <Route path="custom-api" element={<RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="customapi"><CustomAPIPage /></IntegrationRoute></RoleRoute>} />
                                      <Route path="add-customer" element={<RoleRoute allowed={["ADMIN"]}><AddCustomer /></RoleRoute>} />
                                      <Route path="team-management" element={<FeatureRoute featureKey="team_management"><RoleRoute allowed={["ADMIN"]}><TeamManagement /></RoleRoute></FeatureRoute>} />
                                      <Route path="business-accounts" element={<FeatureRoute featureKey="business_accounts"><RoleRoute allowed={["ADMIN"]}><BusinessAccounts /></RoleRoute></FeatureRoute>} />
                                      <Route path="notifications" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><Notifications /></RoleRoute>} />
                                      <Route path="V3/design-system" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><DesignSystem /></RoleRoute>} />
                                      {/* Live-chat CHANNELS list stays routed. The sidebar points at the
                                          unified workspace (`/home/ai-agents?type=general`) per D1, but this
                                          page owns the only validated channel create/edit/delete flow —
                                          required website URL, URL normalisation, and the duplicate-name
                                          guard that exists because the live-chat microservice has no unique
                                          constraint on `channel_name`. Redirecting it away left NO path to
                                          create a channel anywhere in the app, and turned the "Create
                                          Channel" button on the agent detail page into a dead end.
                                          Folded into the merged editor in P1.2. */}
                                      <Route path="live-chat" element={<RoleRoute allowed={["ADMIN"]}><Channels /></RoleRoute>} />
                                      <Route path="scheduling" element={<RoleRoute allowed={["ADMIN", "USER"]}><Scheduling initialTab="event-types" hideTabs={true} /></RoleRoute>} />
                                      <Route path="live-chat/:scriptId" element={<RoleRoute allowed={["ADMIN"]}><LiveChat /></RoleRoute>} />
                                      <Route path="ai-agents" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><ManageAiAgents /></RoleRoute>} />
                                      <Route path="ai-agents/:id" element={<RoleRoute allowed={["ADMIN"]}><AiAgentDetail /></RoleRoute>} />
                                      <Route path="chatbot" element={<RoleRoute allowed={["ADMIN"]}><Chatbot /></RoleRoute>} />
                                      <Route path="form-data" element={<FeatureRoute featureKey="whatsapp_flows"><RoleRoute allowed={["ADMIN", "USER"]}><FormDataStore /></RoleRoute></FeatureRoute>} />
                                      <Route path="data-stores" element={<FeatureRoute featureKey="data_store"><RoleRoute allowed={["ADMIN"]}><DataStoreManager /></RoleRoute></FeatureRoute>} />
                                      <Route path="data-stores/:id" element={<FeatureRoute featureKey="data_store"><RoleRoute allowed={["ADMIN"]}><DataStoreDetail /></RoleRoute></FeatureRoute>} />
                                      <Route path="google-sheets-data" element={<RoleRoute allowed={["ADMIN", "USER"]}><GoogleSheetsData /></RoleRoute>} />
                                      <Route path="plans" element={<RoleRoute allowed={["ADMIN"]}><PlansAndSubscriptions /></RoleRoute>} />
                                      <Route path="boards/leads" element={<RoleRoute allowed={["ADMIN", "USER"]}><BoardDashboard boardType="leads" /></RoleRoute>} />
                                      <Route path="boards/customers" element={<RoleRoute allowed={["ADMIN", "USER"]}><BoardDashboard boardType="customers" /></RoleRoute>} />
                                      <Route path="boards/:id" element={<RoleRoute allowed={["ADMIN", "USER"]}><KanbanBoard /></RoleRoute>} />
                                      <Route path="boards" element={<RoleRoute allowed={["ADMIN", "USER"]}><BoardDashboard /></RoleRoute>} />
                                      <Route path="instagram-broadcasts" element={<FeatureRoute featureKey="instagram_broadcasts"><RoleRoute allowed={["ADMIN"]}><InstagramBroadcasts /></RoleRoute></FeatureRoute>} />
                                      <Route path="scheduling-posts" element={<FeatureRoute featureKey="scheduling_posts"><RoleRoute allowed={["ADMIN"]}><SchedulingPosts /></RoleRoute></FeatureRoute>} />
                                      <Route path="meta-ads" element={<FeatureRoute featureKey="meta_ads"><RoleRoute allowed={["ADMIN"]}><MetaAds /></RoleRoute></FeatureRoute>} />
                                      <Route path="lead-ads-sync" element={<FeatureRoute featureKey="lead_ads_sync"><RoleRoute allowed={["ADMIN"]}><LeadAdsSync /></RoleRoute></FeatureRoute>} />
                                      <Route path="whatsapp-ads" element={<FeatureRoute featureKey="whatsapp_ads"><RoleRoute allowed={["ADMIN"]}><WhatsAppAds /></RoleRoute></FeatureRoute>} />
                                      <Route path="scheduling-overview" element={<RoleRoute allowed={["ADMIN", "USER"]}><SchedulingOverview /></RoleRoute>} />
                                      <Route path="calendly" element={<FeatureRoute featureKey="calendly"><RoleRoute allowed={["ADMIN"]}><Calendly /></RoleRoute></FeatureRoute>} />
                                      <Route path="time-slots" element={<FeatureRoute featureKey="time_slots"><RoleRoute allowed={["ADMIN"]}><TimeSlots /></RoleRoute></FeatureRoute>} />
                                      <Route path="meeting-links" element={<FeatureRoute featureKey="meeting_links"><RoleRoute allowed={["ADMIN"]}><MeetingLinks /></RoleRoute></FeatureRoute>} />
                                      <Route path="scheduling-contacts" element={<RoleRoute allowed={["ADMIN", "USER"]}><SchedulingContacts /></RoleRoute>} />
                                      <Route path="whatsapp-reminders" element={<FeatureRoute featureKey="appointments"><RoleRoute allowed={["ADMIN"]}><WhatsAppReminders /></RoleRoute></FeatureRoute>} />
                                      <Route path="scheduling-integrations" element={<RoleRoute allowed={["ADMIN", "USER"]}><SchedulingIntegrations /></RoleRoute>} />
                                      <Route path="orders" element={<FeatureRoute featureKey="orders"><RoleRoute allowed={["ADMIN"]}><Orders /></RoleRoute></FeatureRoute>} />
                                      <Route path="orders/:orderId" element={<FeatureRoute featureKey="orders"><RoleRoute allowed={["ADMIN"]}><OrderDetail /></RoleRoute></FeatureRoute>} />
                                      <Route path="products" element={<FeatureRoute featureKey="products"><RoleRoute allowed={["ADMIN"]}><Products /></RoleRoute></FeatureRoute>} />
                                      <Route path="abonded-carts" element={<FeatureRoute featureKey="abonded_carts"><RoleRoute allowed={["ADMIN"]}><AbondedCarts /></RoleRoute></FeatureRoute>} />
                                      <Route path="payments" element={<FeatureRoute featureKey="payments"><RoleRoute allowed={["ADMIN"]}><Payments /></RoleRoute></FeatureRoute>} />
                                      <Route path="store-reports" element={<FeatureRoute featureKey="store_reports"><RoleRoute allowed={["ADMIN"]}><StoreReports /></RoleRoute></FeatureRoute>} />
                                      <Route path="invoices" element={<RoleRoute allowed={["ADMIN"]}><Invoices /></RoleRoute>} />
                                      {/* CRM is consolidated into the board (/home/boards/leads),
                                          which hosts Leads/Deals/Tickets tabs. The standalone
                                          Leads & Customers list pages are retired — redirect any
                                          direct hit (bookmarks, stale links) to the board. */}
                                      <Route path="leads" element={<Navigate to="/home/boards/leads" replace />} />
                                      <Route path="customers" element={<Navigate to="/home/boards/leads" replace />} />
                                      <Route path="segments" element={<FeatureRoute featureKey="segments"><RoleRoute allowed={["ADMIN"]}><Segments /></RoleRoute></FeatureRoute>} />
                                      <Route path="chat-history" element={<FeatureRoute featureKey="chat_history"><RoleRoute allowed={["ADMIN"]}><ChatHistory /></RoleRoute></FeatureRoute>} />
                                      {/* Notes & Tags retired into the board (tags via the board's
                                          Tags drawer, notes per-contact) — redirect to the board. */}
                                      <Route path="notes-and-tags" element={<Navigate to="/home/boards/leads" replace />} />
                                      <Route path="message-reports" element={<FeatureRoute featureKey="message_reports"><RoleRoute allowed={["ADMIN"]}><MessageReports /></RoleRoute></FeatureRoute>} />
                                      <Route path="campaign-performance" element={<FeatureRoute featureKey="campaign_performance"><RoleRoute allowed={["ADMIN"]}><CampaignPerformance /></RoleRoute></FeatureRoute>} />
                                      <Route path="ads-results" element={<FeatureRoute featureKey="ads_results"><RoleRoute allowed={["ADMIN"]}><AdsResults /></RoleRoute></FeatureRoute>} />
                                      <Route path="sales-and-conversions" element={<FeatureRoute featureKey="sales_and_conversions"><RoleRoute allowed={["ADMIN"]}><SalesAndConversions /></RoleRoute></FeatureRoute>} />
                                      <Route path="how-to-use" element={<FeatureRoute featureKey="how_to_use"><RoleRoute allowed={["ADMIN"]}><HowToUse /></RoleRoute></FeatureRoute>} />
                                      <Route path="updates" element={<RoleRoute allowed={["ADMIN", "USER", "AGENT"]}><WhatsNew /></RoleRoute>} />
                                      {/* Old changelog URL — keep resolving so bookmarks / sent
                                          links land on Updates instead of a dead route. */}
                                      <Route path="whats-new" element={<Navigate to="/home/updates" replace />} />
                                      <Route path="help-articles" element={<FeatureRoute featureKey="help_articles"><RoleRoute allowed={["ADMIN"]}><HelpArticles /></RoleRoute></FeatureRoute>} />
                                      <Route path="raise-ticket" element={<FeatureRoute featureKey="raise_ticket"><RoleRoute allowed={["ADMIN"]}><RaiseTickets /></RoleRoute></FeatureRoute>} />
                                      <Route path="contact-support" element={<FeatureRoute featureKey="contact_support"><RoleRoute allowed={["ADMIN"]}><ContactSupport /></RoleRoute></FeatureRoute>} />

                                      {/* Social Media Routes */}
                                      {/* Redirect only — the page moved to /home/integrations?tab=social.
                                          No FeatureRoute here: the target route owns the feature gate,
                                          and double-gating would bounce an org that has social_media_posts
                                          but not integrations off to /home instead of the tab. */}
                                      <Route path="social-accounts" element={<RoleRoute allowed={["ADMIN"]}><SocialAccountsRedirect /></RoleRoute>} />
                                      <Route path="social-inbox" element={<RoleRoute allowed={["ADMIN"]}><SocialInbox /></RoleRoute>} />
                                      <Route path="social-media/posts" element={<RoleRoute allowed={["ADMIN"]}><SocialMediaPostsManager /></RoleRoute>} />
                                      <Route path="social-media/posts/create" element={<RoleRoute allowed={["ADMIN"]}><CreatePost /></RoleRoute>} />
                                      <Route path="social-media/posts/create-global" element={<RoleRoute allowed={["ADMIN"]}><SocialMediaPostsManager /></RoleRoute>} />
                                      <Route path="social-media/instagram" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramAccounts /></RoleRoute></FeatureRoute>} />
                                      <Route path="social-media/youtube" element={<FeatureRoute featureKey="youtube_integration"><RoleRoute allowed={["ADMIN"]}><YouTubeChannels /></RoleRoute></FeatureRoute>} />
                                      <Route path="social-media/youtube/content" element={<FeatureRoute featureKey="youtube_integration"><RoleRoute allowed={["ADMIN"]}><Navigate to="/home/social-media/posts?platform=youtube" replace /></RoleRoute></FeatureRoute>} />
                                      <Route path="social-media/youtube/comments" element={<FeatureRoute featureKey="youtube_integration"><RoleRoute allowed={["ADMIN"]}><YouTubeComments /></RoleRoute></FeatureRoute>} />
                                      <Route path="social-media/youtube/insights" element={<FeatureRoute featureKey="youtube_integration"><RoleRoute allowed={["ADMIN"]}><YouTubeInsights /></RoleRoute></FeatureRoute>} />
                                      <Route path="social-media/youtube/automation" element={<FeatureRoute featureKey="youtube_integration"><RoleRoute allowed={["ADMIN"]}><YouTubeAISettings /></RoleRoute></FeatureRoute>} />
                                      {/* Legacy YouTube URLs → Social Media */}
                                      <Route path="youtube" element={<Navigate to="/home/social-media/youtube" replace />} />
                                      <Route path="youtube/content" element={<Navigate to="/home/social-media/posts?platform=youtube" replace />} />
                                      <Route path="youtube/comments" element={<Navigate to="/home/social-media/youtube/comments" replace />} />
                                      <Route path="youtube/insights" element={<Navigate to="/home/social-media/youtube/insights" replace />} />
                                      <Route path="youtube/automation" element={<Navigate to="/home/social-media/youtube/automation" replace />} />
                                      <Route path="social-media/facebook" element={<FeatureRoute featureKey="facebook_posts"><RoleRoute allowed={["ADMIN"]}><FacebookAccounts /></RoleRoute></FeatureRoute>} />
                                      <Route path="social-media/linkedin" element={<FeatureRoute featureKey="linkedin_posts"><RoleRoute allowed={["ADMIN"]}><LinkedInAccounts /></RoleRoute></FeatureRoute>} />
                                      <Route path="social-media/tiktok" element={<FeatureRoute featureKey="tiktok_content"><RoleRoute allowed={["ADMIN"]}><TikTokAccounts /></RoleRoute></FeatureRoute>} />
                                      <Route path="social-media/linkedin/select-account" element={<FeatureRoute featureKey="linkedin_posts"><RoleRoute allowed={["ADMIN"]}><LinkedInSelectAccount /></RoleRoute></FeatureRoute>} />

                                      {/* Instagram Routes — gated by instagram_automation feature */}
                                      <Route path="instagram-automation" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramAutomationSelector /></RoleRoute></FeatureRoute>} />
                                      <Route path="instagram/posts" element={<InstagramPostsLegacyRedirect />} />
                                      <Route path="instagram/inbox" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramInbox /></RoleRoute></FeatureRoute>} />
                                      <Route path="instagram/insights" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramInsights /></RoleRoute></FeatureRoute>} />
                                      <Route path="instagram/orders" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><InstagramOrders /></RoleRoute></FeatureRoute>} />
                                      {/* <Route path="instagram/automation" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><AutomationFlows /></RoleRoute></FeatureRoute>} /> */}
                                      <Route path="instagram/automation/create" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><CreateAutomationFlow /></RoleRoute></FeatureRoute>} />
                                      <Route path="instagram/automation/builder" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><AutomationFlowBuilder /></RoleRoute></FeatureRoute>} />
                                      <Route path="instagram/automation/leads" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><AutomationLeads /></RoleRoute></FeatureRoute>} />
                                      <Route path="instagram/automation/logs/:flowId" element={<FeatureRoute featureKey="instagram_automation"><RoleRoute allowed={["ADMIN"]}><AutomationLogs /></RoleRoute></FeatureRoute>} />

                                      {/* GenUI Component Showcase */}
                                      <Route path="genui-components" element={<RoleRoute allowed={["ADMIN"]}><GenUIComponentsPage /></RoleRoute>} />

                                      {/* Facebook Posts Route — legacy standalone page redirected to the
                                          unified Social Media Posts workspace, same as Instagram/LinkedIn. */}
                                      <Route path="facebook/posts" element={<FacebookPostsLegacyRedirect />} />
                                      {/* Legacy LinkedIn Posts URL → the LinkedIn tab of the Social
                                          Media Posts workspace, which renders the same page. The
                                          standalone sidebar entry was removed 2026-08-04 as a
                                          duplicate; keeping the path as a redirect preserves old
                                          bookmarks and keeps the destination menu-permission gated
                                          (see the note in AppSidebar.primaryMenuItems). */}
                                      <Route path="linkedin/posts" element={<Navigate to="/home/social-media/posts?platform=linkedin" replace />} />

                                      {/* CTWA Ads Routes */}
                                      <Route path="ads" element={<RoleRoute allowed={["ADMIN"]}><MetaAdsPage /></RoleRoute>} />
                                      <Route path="ads/setup" element={<RoleRoute allowed={["ADMIN"]}><AdsSetup /></RoleRoute>} />
                                      <Route path="ads/create" element={<RoleRoute allowed={["ADMIN"]}><AdCreate /></RoleRoute>} />
                                      <Route path="ads/:id" element={<RoleRoute allowed={["ADMIN"]}><AdDetail /></RoleRoute>} />
                                      <Route path="ads/ai-builder" element={<RoleRoute allowed={["ADMIN"]}><AiAdBuilder /></RoleRoute>} />
                                      <Route path="ads/leads" element={<RoleRoute allowed={["ADMIN"]}><MetaLeads /></RoleRoute>} />
                                      <Route path="ads/attribution" element={<RoleRoute allowed={["ADMIN"]}><AdAttribution /></RoleRoute>} />
                                      <Route path="ads/campaign/:metaCampaignId" element={<RoleRoute allowed={["ADMIN"]}><CampaignDetail /></RoleRoute>} />
                                      <Route path="ads/adset/:metaAdSetId" element={<RoleRoute allowed={["ADMIN"]}><AdSetDetail /></RoleRoute>} />
                                      <Route path="ads/meta/:metaAdId" element={<RoleRoute allowed={["ADMIN"]}><MetaAdDetail /></RoleRoute>} />
                                      <Route path="ads/experiments" element={<RoleRoute allowed={["ADMIN"]}><Experiments /></RoleRoute>} />
                                      <Route path="ads/experiments/:id" element={<RoleRoute allowed={["ADMIN"]}><ExperimentDetail /></RoleRoute>} />

                                      {/* LinkedIn Ads Routes */}
                                      <Route path="linkedin-ads" element={<RoleRoute allowed={["ADMIN"]}><LinkedInAdsManager /></RoleRoute>} />
                                      <Route path="linkedin-ads/setup" element={<RoleRoute allowed={["ADMIN"]}><LinkedInAdsSetup /></RoleRoute>} />
                                      <Route path="linkedin-ads/create" element={<RoleRoute allowed={["ADMIN"]}><LinkedInAdCreate /></RoleRoute>} />
                                      <Route path="linkedin-ads/campaign/:campaignId" element={<RoleRoute allowed={["ADMIN"]}><LinkedInCampaignDetail /></RoleRoute>} />
                                      <Route path="linkedin-ads/campaign/:campaignId/edit" element={<RoleRoute allowed={["ADMIN"]}><LinkedInCampaignEdit /></RoleRoute>} />
                                      <Route path="linkedin-ads/analytics" element={<RoleRoute allowed={["ADMIN"]}><LinkedInAdsAnalytics /></RoleRoute>} />
                                      <Route path="linkedin-ads/ad-library" element={<RoleRoute allowed={["ADMIN"]}><LinkedInAdLibrary /></RoleRoute>} />
                                      <Route path="linkedin-ads/events" element={<RoleRoute allowed={["ADMIN"]}><LinkedInEvents /></RoleRoute>} />
                                      <Route path="linkedin-ads/verified" element={<RoleRoute allowed={["ADMIN"]}><LinkedInVerified /></RoleRoute>} />

                                      {/* PetPooja is granted at TOP-LEVEL `features.petpooja`, never at
                                          `sub_features.integrations.petpooja` (see lib/integrationEntitlements).
                                          `alsoAllowFeatureKey` is therefore required: with the parent
                                          `integrations` feature off but petpooja granted, the sidebar entry and
                                          the integration card both render, and a `featureKey="integrations"`-only
                                          guard would bounce the click straight back to /home. Route access and
                                          card visibility must agree. */}
                                      <Route path="petpooja" element={<FeatureRoute featureKey="integrations" alsoAllowFeatureKey="petpooja"><RoleRoute allowed={["ADMIN"]}><IntegrationRoute entitlementKey="petpooja"><PetpoojaDashboard /></IntegrationRoute></RoleRoute></FeatureRoute>} />
                                      <Route path="petpooja/orders" element={<FeatureRoute featureKey="integrations" alsoAllowFeatureKey="petpooja"><RoleRoute allowed={["ADMIN"]}><PetpoojaOrders /></RoleRoute></FeatureRoute>} />
                                      <Route path="tiktok-ads" element={<FeatureRoute featureKey="integrations"><RoleRoute allowed={["ADMIN"]}><TikTokAdsDashboard /></RoleRoute></FeatureRoute>} />

                                      <Route path="*" element={<NotFound />} />
                                    </Routes>
                                    </Suspense>
                                  </AppShell>
                                </NotificationProvider>
                              </MessageStatusProvider>
                            </UnreadMessagesProvider>
                            </CallProvider>
                          </ChatProvider>
                        </BusinessAccountProvider>
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } />

                  {/* Fallback for any unmatched routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </NavigationProvider>
            </BrowserRouter>
          </WidgetConfigProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
